import { tools } from '@/utils/tools';
import { defineAsyncComponent, h } from 'vue';
import { IconLoading } from '@arco-design/web-vue/es/icon';
import { setService } from '@/api';

import fieldPermission from '@/hooks/field-permission';
const { isRequiredField } = fieldPermission();

const useTableConfig = (entity) => {
  const FhFieldShow = defineAsyncComponent({
    loader: () => import('@/components/fh-set-fields/field-list-main/index.vue'),
    loadingComponent: IconLoading
  });
  const strLenCache = new Map();

  const getStrLen = (str) => {
    if (!str) return 0;
    if (strLenCache.has(str)) {
      return strLenCache.get(str);
    }
    const len = tools.getStrLen(str);
    strLenCache.set(str, len);
    // 防止缓存过大，当缓存超过500项时清理
    if (strLenCache.size > 500) {
      // 清除最早的100项
      const keys = Array.from(strLenCache.keys()).slice(0, 100);
      keys.forEach((key) => strLenCache.delete(key));
    }
    return len;
  };

  // 创建列配置
  const createColumnConfig = (item, entityId = null, businessData = null, fn = undefined, tableLevel) => {
    const _entityId = entityId === null ? entity.value : entityId;
    const k = item.fieldKey;
    const fieldType = item.fieldType == 21 ? item?.business?.fieldType ?? 21 : item?.fieldType;
    const fieldExt = item.fieldType == 21 ? item?.business?.fieldExt : item?.fieldExt;

    const defaultWidth = [8, 9].includes(fieldType) ? 160 : getStrLen(item.fieldName) <= 6 ? 120 : getStrLen(item.fieldName) > 10 ? 220 : getStrLen(item.fieldName) * 20;
    return {
      colKey: k,
      title: item.fieldName,
      fieldName: item.fieldName,
      fieldType: fieldType,
      fieldExt: fieldExt,
      fieldSort: item.fieldSort,
      width: item.mainField || [12, 25].includes(fieldType) ? 250 : defaultWidth,
      resize: { minWidth: [8, 9].includes(fieldType) ? 160 : getStrLen(item.fieldName) <= 5 ? 100 : 150 },
      fixed: businessData === '1' ? 'left' : undefined,
      align: [7, 16, 23, 24].includes(fieldType) ? 'right' : 'left',
      iconMore: false,
      colType: item.type === 1 ? '基础字段' : '其他',
      isSubForm: false,
      render: (row) => {
        const params = {
          col: item,
          tableLevel: tableLevel,
          row,
          fieldKey: k,
          mainField: item.mainField,
          isRequired: isRequiredField(item.fieldKey, row?.PERMISSION, item?.isRequired),
          fieldValue: row[k],
          fieldType: fieldType,
          fieldExt: fieldExt,
          entity: _entityId,
          // isEdit: isEditTableRow(fieldType, k, item),
          sourceType: 1, //1:列表 2：详情
          isSubForm: false,
          isAutoHeight: true,
          permissions: row.LOGIC_ROLE_PERMISSION,
          PERMISSION: row?.PERMISSION
        };
        return h(FhFieldShow, {
          params,
          onCallbackList: (callback) => {
            if (callback?.businessId && fn !== undefined) {
              fn(callback);
            }
          }
        });
      }
    };
  };

  //获取多层级对应id
  const getTableLevelEntity = (modules) => {
    return modules?.businessData || '';
  };
  //获取多层级对应entity
  const getObjEntity = (multiTable, isExpand) => {
    return isExpand ? multiTable?.businessData : entity.value || multiTable?.businessData || '';
  };

  // 获取子表单字段
  const getSubFormField = (fieldVos) => {
    return fieldVos?.map((v) => ({
      fieldKey: v.fieldKey,
      entityId: v.entityId,
      id: v.id,
      fieldType: v.fieldType
    }));
  };

  // 创建子表单列配置
  const createChildColumns = (item, tableLevel) => {
    const colTypeChildren = [];
    item?.fieldVos?.forEach((el) => {
      const defaultCellWidth = [8, 9].includes(el.fieldType) ? 160 : getStrLen(el.fieldName) <= 6 ? 120 : getStrLen(el.fieldName) > 10 ? 220 : getStrLen(el.fieldName) * 20;
      const childRender = (row) => {
        const record = row;
        const subFormData = record?.[item?.fieldKey] || [];
        if (subFormData?.length) {
          return h('div', { class: 'child-table-container' }, [
            ...subFormData.map((subFormRecord) => {
              const params = {
                col: { ...el, setting: { ...el } },
                row: subFormRecord,
                fieldKey: el.fieldKey,
                mainField: el.mainField,
                isRequired: el.isRequired,
                fieldValue: subFormRecord?.[el.fieldKey],
                fieldType: el.fieldType,
                fieldExt: el.fieldExt,
                entity: entity.value,
                subEntityId: item.fieldExt,
                // isEdit: isEditSubTableRow(el),
                isSubForm: true,
                sourceType: 1, //1:列表 2：详情
                tableLevel: tableLevel,
                isAutoHeight: false,
                permissions: subFormRecord.LOGIC_ROLE_PERMISSION
              };
              return h(FhFieldShow, { params });
            })
          ]);
        } else {
          return h('span', { class: 'child-cell-empty' }, '-');
        }
      };
      const getColType = {
        colKey: `${item.fieldKey}.${el.fieldKey}`,
        title: el.fieldName,
        fieldName: el.fieldName,
        fieldType: el.fieldType,
        fieldExt: el.fieldExt,
        width: defaultCellWidth,
        resize: { minWidth: [8, 9].includes(el.fieldType) ? 160 : getStrLen(el.fieldName) <= 5 ? 100 : 150 },
        iconMore: true,
        isSubForm: true,
        align: [7, 16, 23, 24].includes(el.fieldType) ? 'right' : 'left',
        setting: item?.fieldVos?.length ? getSubFormField(item.fieldVos) : null,
        render: childRender
      };
      colTypeChildren.push(getColType);
    });
    return colTypeChildren;
  };

  // 从所有字段中获取表格头部标题
  const getColumns = (multiTable, { updateFieldPermission: fn }, tableLevel) => {
    const start = performance.now();
    const list = [];
    const _fieldList = multiTable?.modules?.filter((item) => item.type == 20); // 获取字段

    _fieldList?.forEach((item) => {
      const _tmpItem = item?.setting;
      const colConfig = createColumnConfig(_tmpItem, getTableLevelEntity(multiTable), item?.businessData, fn, tableLevel);
      if (_tmpItem?.fieldType == 27) {
        colConfig.children = createChildColumns(_tmpItem, fn, tableLevel);
      }
      list.push(colConfig);
    });
    console.log(123456, `getColumns方法耗时: ${performance.now() - start}ms`);
    return list;
  };

  //组装表格数据 表格数据 计算字段数据
  const AssTableData = (tableData, computedInfoList) => {
    const resultData = [...tableData];
    resultData.forEach((itemA) => {
      const matchingItem = computedInfoList?.find((itemB) => itemB.businessId === itemA.id);
      if (matchingItem) {
        // 如果找到了匹配项，就把 computed 的值添加到 数据 中
        itemA.computedInfo = matchingItem;
      } else {
        itemA.computedInfo = null;
      }
    });
    return resultData;
  };

  //获取计算状态
  const getComputedErrorLog = async (businessIdList, entityParams) => {
    try {
      const json = await setService.getComputedErrorLog({
        entity: entityParams,
        businessIdList: businessIdList
      });
      if (!json.error) {
        const result = json.result;
        return result;
      }
    } catch (error) {
      console.log(error);
    }
  };

  return { getColumns, getTableLevelEntity, getObjEntity, getComputedErrorLog, AssTableData };
};

export { useTableConfig };
